import { markHostedBootWait } from '#~/routes/root/boot-budget.js';
import { readSessionReturn } from '#~/auth/session-return.js';
import { fail, OAuthTimeout } from '#~/auth/index.js';
import type { LocationState } from '#~/auth/index.js';
import {
  buildHostedAuth0ExtParams,
  getLastKnownUserId,
  makeAuthJourneyAppState,
  resolveAuthJourneyForRoute,
  setActiveAuthJourney
} from '#~/auth/index.js';
import {
  EVENT_CATEGORIES,
  EVENT_NAMES,
  trackAuthLoginFailed,
  useAnalytics
} from '#~/analytics/index.js';
import { Paths } from '../constants.js';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader } from '@studio/design-system/loader';
import { toString, winnowObject } from '@tryiris-ai/js-types';
import pTimeout from 'p-timeout';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const Inner = ({
  callbackUri,
  locationState,
  redirectUri
}: {
  callbackUri: string;
  locationState?: LocationState;
  redirectUri: string;
}): ReactElement => {
  const { loginWithRedirect } = useAuth0();
  const { track } = useAnalytics();

  useEffect(() => {
    const journey = resolveAuthJourneyForRoute('studio_login', locationState);

    setActiveAuthJourney(journey);

    track(EVENT_NAMES.LOGIN_SCREEN_LOADED, EVENT_CATEGORIES.AUTH, {
      auth_journey_id: journey.authJourneyId,
      source: journey.source
    });

    if (getLastKnownUserId()) {
      track(EVENT_NAMES.PREVIOUSLY_AUTHENTICATED_USER_REACHED_LOGIN, EVENT_CATEGORIES.AUTH, {
        auth_journey_id: journey.authJourneyId,
        source: journey.source
      });
    }

    const cancelHandoff = markHostedBootWait();
    pTimeout(
      loginWithRedirect(
        winnowObject({
          appState: makeAuthJourneyAppState(redirectUri, journey),
          authorizationParams: winnowObject({
            ...buildHostedAuth0ExtParams(journey),
            redirect_uri: callbackUri
          })
        })
      ),
      {
        milliseconds: OAuthTimeout
      }
    ).catch((error: Error) => {
      cancelHandoff();
      // ENG-4348 stage 3: dual-emits auth_login_failed + legacy oauth_error
      // through the shared helper (journey props injected there) so this site
      // can't drift from the canonical pair during the parity window.
      trackAuthLoginFailed(track, journey, { error_message: error.message });
      throw fail('6E4C', error);
    });
  }, [callbackUri, locationState, loginWithRedirect, redirectUri, track]);

  return <Loader />;
};

/** `<Login>` component. */
export const Login = (): ReactElement => {
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const urlBase = `${window.location.protocol}//${window.location.host}`;
  const locationState = location.state as LocationState;

  let redirectUri =
    readSessionReturn()?.returnTo ?? toString(locationState?.redirect, { default: '/' });
  const callbackUri = `${urlBase}/${Paths.oauth2.callback}`;

  if (window.location.hash.startsWith('#r=')) {
    const returnTo = window.location.hash.split('r=')[1]?.split('&')[0];

    if (returnTo) {
      redirectUri = (returnTo.startsWith('/') ? '' : '/') + returnTo;
    }
  }

  // Is the user already authenticated?
  return isAuthenticated ? (
    <Navigate replace to={redirectUri} />
  ) : (
    <Inner callbackUri={callbackUri} locationState={locationState} redirectUri={redirectUri} />
  );
};
